import type { MouseEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Link as MuiLink } from '@mui/material';
import { getGridDateOperators, GridColDef } from '@mui/x-data-grid-pro';
import moment from 'moment';

import { DATE_FORMAT, TIME_FORMAT } from '@/config';
import { ICFS, IEquipment, IUser, IVehicle } from '@/models';
import { getTimeDifference, openNewWindow } from '@/utils';

const unitName = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.unit.name || '-',
});

const users = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    params.row.users.map((u: IUser) => u.fullName).join(', '),
});

const vehicles = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    params.row.vehicles.map((v: IVehicle) => v.name).join(', '),
});

const equipments = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    params.row.equipments.map((e: IEquipment) => e.name).join(', '),
});

const status = (): Partial<GridColDef> => ({
  field: 'status',
  renderCell: (params) => params.row.status || '-',
});

const startAt = (): Partial<GridColDef> => ({
  field: 'startAt',
  renderCell: (params) => {
    return params.row.startAt
      ? `${moment(params.row.startAt).format(DATE_FORMAT)} • ${moment(
          params.row.startAt,
        ).format(TIME_FORMAT)}`
      : '-';
  },
  filterOperators: getGridDateOperators(true),
});

const endAt = (): Partial<GridColDef> => ({
  field: 'endAt',
  renderCell: (params) => {
    return params.row.endAt
      ? `${moment(params.row.endAt).format(DATE_FORMAT)} • ${moment(
          params.row.endAt,
        ).format(TIME_FORMAT)}`
      : '-';
  },
  filterOperators: getGridDateOperators(true),
});

const duration = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const { startAt: start, endAt: end } = params.row;

    if (!start && !end) {
      return '-';
    }

    if (!end) {
      return getTimeDifference(moment(), new Date(start)).format('HH:mm:ss');
    }

    return getTimeDifference(moment(end), new Date(start)).format('HH:mm:ss');
  },
  filterable: false,
});

const cfs = (cfsDisplayOption?: string): Partial<GridColDef> => ({
  field: 'cfs',
  renderCell: (params) => {
    const cfsList: ICFS[] = params.row?.cfs || [];
    if (!cfsList.length) return '-';

    const shouldOpenNewWindow = cfsDisplayOption === 'new-window';

    return cfsList.map((item, idx) => (
      <span key={item._id}>
        <MuiLink
          component={shouldOpenNewWindow ? 'button' : RouterLink}
          to={shouldOpenNewWindow ? undefined : `/cfs/${item._id}`}
          state={
            shouldOpenNewWindow
              ? undefined
              : {
                  from: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                }
          }
          underline="hover"
          sx={{ cursor: 'pointer', color: 'red' }}
          onClick={(e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            if (shouldOpenNewWindow) {
              e.preventDefault();
              void openNewWindow(`/cfs/${item._id}`, item._id);
            }
          }}
        >
          {item.number}
        </MuiLink>
        {idx < cfsList.length - 1 ? ', ' : ''}
      </span>
    ));
  },
  valueGetter: (_, row) =>
    row.cfs?.length ? row.cfs.map((item: ICFS) => item.number).join(', ') : '-',
});

export const unitShiftsHistoryCols = (cfsDisplayOption?: string) => {
  return {
    unitName,
    users,
    vehicles,
    equipments,
    status,
    startAt,
    endAt,
    duration,
    cfs: () => cfs(cfsDisplayOption),
  };
};
